class GroupedProductsPicker extends HTMLElement {
  constructor() {
    super();
    this.matrix = {};
    this.currentType = '';
    this.currentHeight = '';
    this.currentUrl = '';
    this.typeGroups = new Map();
  }

  connectedCallback() {
    try {
      const rawMatrix = JSON.parse(this.dataset.matrix);
      this.currentType = rawMatrix.current.type;
      this.currentHeight = rawMatrix.current.height;
      this.currentUrl = rawMatrix.current.url;

      // Transform the data into type-grouped format
      this.transformData(rawMatrix);

      console.log('Type groups:', this.typeGroups);
      this.innerHTML = this.createTemplate();

      const typeModal = document.querySelector('[data-type-modal]');
      const heightModal = document.querySelector('[data-height-modal]');

      if (typeModal) {
        const typeHeader = this.querySelector('.types-column .grouped-products__title');
        typeHeader.after(typeModal);
      }

      if (heightModal) {
        const heightHeader = this.querySelector('.sizes-column .grouped-products__title');
        heightHeader.after(heightModal);
      }
    } catch (e) {
      console.warn('Failed to parse product matrix:', e);
      return;
    }
  }

  transformData(rawMatrix) {
    // Initialize the type groups map
    this.typeGroups = new Map();

    // Process each size and its types
    for (const size of rawMatrix.sizes) {
      // Process each type in the size
      for (const typeData of size.types) {
        const type = typeData.type;

        if (!this.typeGroups.has(type)) {
          this.typeGroups.set(type, {
            sizes: new Map(),
            available: true,
            icon: typeData.icon,
            url: typeData.url,
          });
        }

        const typeGroup = this.typeGroups.get(type);

        // Add the current size
        if (typeData.available) {
          typeGroup.sizes.set(size.height, {
            height: size.height,
            url: typeData.url, // Use the type's URL
            available: typeData.available,
          });

          // Add all sizes from the type's sizes array
          for (const linkedSize of typeData.sizes) {
            typeGroup.sizes.set(linkedSize.height, {
              height: linkedSize.height,
              url: linkedSize.url,
              available: linkedSize.available,
            });
          }
        }
      }
    }

    // Sort sizes within each type
    for (const [type, data] of this.typeGroups) {
      const sortedSizes = new Map(
        [...data.sizes.entries()].sort((a, b) => {
          const aSize = parseInt(a[1].height.split('-')[0]);
          const bSize = parseInt(b[1].height.split('-')[0]);
          return aSize - bSize;
        })
      );
      data.sizes = sortedSizes;
    }
  }

  createTemplate() {
    return `
      <div class="flex gap-4 flex-col">
        <div class="types-column w-full">
          <p class="grouped-products__title flex items-center pb-2 text-product-name-xs !leading-[120%]">
            <span>${this.dataset.type || ''}</span>
            <span class="grouped-products__titled-value">
              <span>:</span>
              <span class="text-sm font-normal leading-[120%]">${this.currentType || ''}</span>
            </span>
          </p>

          <ul class="grouped-products__list flex flex-wrap gap-2">
            ${[...this.typeGroups.entries()]
              .sort(([typeA], [typeB]) =>
                typeA.localeCompare(typeB, undefined, { sensitivity: 'base' })
              )
              .map(([type, data], index) => {
                const isAvailable = data.available;
                let targetUrl = '#';

                if (type === this.currentType) {
                  targetUrl = this.currentUrl;
                } else if (isAvailable) {
                  const getFirstNumber = (size) => parseInt(size.match(/^(\d+)/)?.[1] || 0);

                  const findClosestSize = (currentSize, availableSizes) => {
                    const currentNum = getFirstNumber(currentSize);
                    let [closest, minDiff] = [null, Infinity];

                    for (const [height, size] of availableSizes) {
                      if (!size.available) continue;
                      const diff = Math.abs(getFirstNumber(height) - currentNum);
                      if (diff < minDiff) [minDiff, closest] = [diff, { height, url: size.url }];
                    }
                    return closest;
                  };

                  const sameSize = data.sizes.get(this.currentHeight);
                  targetUrl = sameSize?.available
                    ? sameSize.url
                    : findClosestSize(this.currentHeight, data.sizes)?.url || '#';
                }

                return `
                <li class="option text-button-md">
                  <a
                    href="${targetUrl}"
                    class="option__label option__label--button option__label--button-type option__label--icon ${
                      type === this.currentType ? 'option__label--checked' : ''
                    } ${!isAvailable ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''}"
                    ${!isAvailable ? 'aria-disabled="true"' : ''}
                  >
                    ${
                      data.icon
                        ? `<span class="icon-wrapper">${this.renderIcon(data.icon)}</span>`
                        : ''
                    }
                    <span>${type}</span>
                  </a>
                </li>
              `;
              })
              .join('')}
          </ul>
        </div>

        <div class="sizes-column w-full">
          <p class="grouped-products__title flex items-center pb-2 text-product-name-xs !leading-[120%]">
            <span>${this.dataset.height || ''}</span>
            <span class="grouped-products__titled-value">
              <span>:</span>
              <span class="text-sm font-normal leading-[120%]">${this.currentHeight || ''}</span>
            </span>
          </p>

          <ul class="grouped-products__list flex flex-wrap gap-2" data-sizes-container>
            ${this.renderSizesForType(this.currentType)}
          </ul>
        </div>
      </div>
    `;
  }

  renderSizesForType(type) {
    const typeGroup = this.typeGroups.get(type);
    if (!typeGroup) return '';

    return [...typeGroup.sizes.values()]
      .map((size) => {
        return `
          <li class="option text-button-md">
            <a
              href="${size.available ? size.url : '#'}"
              class="option__label option__label--button option__label--button-size ${
                size.height === this.currentHeight ? 'option__label--checked' : ''
              } ${!size.available ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''}"
              data-height="${size.height}"
              ${!size.available ? 'aria-disabled="true"' : ''}
            >
              <span>${size.height}</span>
            </a>
          </li>
        `;
      })
      .join('');
  }

  renderIcon(iconName) {
    const iconStore = document.querySelector('[data-icon-store]');
    if (!iconStore) return '';

    const iconWrapper = iconStore.querySelector(`[data-icon="${iconName.toLowerCase()}"]`);
    if (!iconWrapper) return '';

    return iconWrapper.innerHTML;
  }
}

customElements.define('grouped-products-picker', GroupedProductsPicker);
